import React from 'react'
import Employer from '../../components/form/segments/Employer'
import Employee from '../../components/form/segments/Employee'
import Coverage from '../../components/form/segments/Coverage'
import LifeAndDisability from '../../components/form/segments/LifeAndDisability'
import CriticalIllness from '../../components/form/segments/CriticalIllness'
import Life from '../../components/form/segments/Life'
import Disability from '../../components/form/segments/Disability'
import SpouseAndDependents from '../../components/form/segments/SpouseAndDependents'
import Signature from '../../components/form/segments/Signature'
import toMMddyyyy from '../../util/toMMddyyyy'
import {
  CURRENCY_MASK_OPTIONS,
  DATE_MASK_OPTIONS,
  MEDICATION_DATE_RANGE_MASK_OPTIONS,
  NUMERICAL_MASK,
  SSN_MASK_OPTIONS,
  TELEPHONE_MASK_OPTIONS
} from '../../constant/masks'
import _ from 'lodash'
import REQUEST_STATUS from './REQUEST_STATUS'
import toMMddyyyy_slashes from '../../util/toMMddyyyy_slashes'

/**
 * Objects in this file make up the structure of the redux store's form object ({@see initialFormState }).
 * To view an example of the full object structure, see {@link TEST_COMPLETED_FORM_STATE}.
 * (note: The fields in the linked example object have had some properties removed for brevity, but the general structure is intact)
 */


export const field = {
  value: '',
  include: true, // include in the payload sent to the API
  default: '',
  dirty: false,
  disabled: false,
  maskOptions: undefined,
  errorDetail: 'Invalid'
}
export const optionalField = {
  ...field,
  pattern: null,
  required: false,
  valid: true,
}
export const requiredField = {
  ...field,
  pattern: '.',
  required: true,
  valid: false,
}

const formSegment = {
  errored: false,
  completed: false,
  enabled: true,
  attempted: false
}

export const healthInformationFormSegment = {
  ...formSegment,
  maxMedications: 10,
  maxMedicalConditions: 10,
  medicationCount: 0,
  medicalConditionCount: 0,
  fields: {
    healthQuestions: {},
    healthInsuranceQuestions: {},
  },
  childrenIncluded: null,
  underwriting: {
    company: null,
    location: null
  }
}

export const childrenFields = {
  lastName: requiredField,
  firstName: requiredField,
  personal: {
    include: true,
    gender: requiredField,
    birthDate: { ...requiredField, maskOptions: DATE_MASK_OPTIONS },
    weight: { ...requiredField, maskOptions: NUMERICAL_MASK },
    heightFt: requiredField,
    heightIn: requiredField
  }
}

export const medicationFields = {
  applicantName: requiredField,
  medicationName: requiredField,
  dosage: requiredField,
  datesTaken: { ...requiredField, maskOptions: MEDICATION_DATE_RANGE_MASK_OPTIONS },
  reasonForTaking: requiredField
}
export const medicalConditionFields = {
  applicantName: requiredField,
  occurrenceDate: { ...requiredField, maskOptions: DATE_MASK_OPTIONS },
  recoveryDate: { ...requiredField, maskOptions: DATE_MASK_OPTIONS },
  recoveryDegree: requiredField,
  detail: requiredField,
  physicianInformation: requiredField
}

export const lifeCoverageAmounts = {
  lifePlanDesignPk: optionalField,
  lifeElectedEffDate: optionalField,
  lifeCurrent: { ...requiredField, maskOptions: CURRENCY_MASK_OPTIONS },
  lifeAdditional: { ...requiredField, maskOptions: CURRENCY_MASK_OPTIONS },
  lifeTotal: { ...requiredField, include: false, disabled: true, maskOptions: CURRENCY_MASK_OPTIONS }
}

export const ciCoverageAmounts = {
  ciPlanDesignPk: optionalField,
  ciElectedEffDate: optionalField,
  ciCurrent: { ...requiredField, maskOptions: CURRENCY_MASK_OPTIONS },
  ciAdditional: { ...requiredField, maskOptions: CURRENCY_MASK_OPTIONS },
  ciTotal: { ...requiredField, include: false, disabled: true, maskOptions: CURRENCY_MASK_OPTIONS }
}

export const disabilityCoverageAmounts = {
  disabilityType: requiredField,
  stdPlanDesignPk: optionalField,
  ltdPlanDesignPk: optionalField,
  stdElectedEffDate: optionalField,
  ltdElectedEffDate: optionalField,
}

export const baseAddressFields = {
  include: true,
  code1validated: {
    ...requiredField,
    include: false,
    pattern: '^true$'
  },
  suggested: {
    include: false,
    requestStatus: REQUEST_STATUS.NOT_STARTED,
    street: optionalField,
    street2: optionalField,
    city: optionalField,
    state: optionalField,
    zip: optionalField
  },
  street: requiredField,
  street2: optionalField,
  city: requiredField,
  state: requiredField,
  zip: { ...requiredField, pattern: '^\\d{5}(?:[-\\s]\\d{4})?$' },
}

export const personFields = {
  include: true,
  firstName: requiredField,
  lastName: requiredField,
  middleInitial: optionalField,
  telephone: { ...optionalField, maskOptions: TELEPHONE_MASK_OPTIONS },
  email: optionalField,
  electronicSignature: optionalField,
  emailConsent: optionalField,
  address: baseAddressFields,
  otherNames: optionalField,
  employment: {
    include: true,
    fullTimeEmploymentDate: { ...requiredField, maskOptions: DATE_MASK_OPTIONS },
    jobTitle: requiredField,
    avgHoursWorked: { ...optionalField, maskOptions: NUMERICAL_MASK }
  },
  personal: {
    include: true,
    birthDate: { ...requiredField, maskOptions: DATE_MASK_OPTIONS },
    ssnId: { ...optionalField, maskOptions: SSN_MASK_OPTIONS },
    birthState: requiredField,
    gender: requiredField,
    weight: { ...requiredField, maskOptions: NUMERICAL_MASK },
    heightFt: requiredField,
    heightIn: requiredField,
    annualSalary: { ...requiredField, maskOptions: CURRENCY_MASK_OPTIONS }
  }
}

export const spouseFields = {
  ..._.omit(personFields, 'address', 'employment', 'personal'),
  personal: {
    ..._.omit(personFields.personal, 'annualSalary')
  }
}

export const FORM_SEGMENTS = {
  employer: {
    ...formSegment,
    index: 0,
    display: 'Employer',
    panel: <Employer />,
    fields: {
      name: requiredField,
      groupNumber: { ...requiredField, pattern: '^[a-zA-Z0-9]{3,4}$' },
      subgroup: optionalField,
      telephone: { ...optionalField, maskOptions: TELEPHONE_MASK_OPTIONS },
      address: baseAddressFields,
      externalSource: optionalField
    }
  },
  employee: {
    ...formSegment,
    index: 1,
    display: 'Employee',
    panel: <Employee />,
    fields: {
      ...personFields,
      email: { ...requiredField, pattern: '^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,}$' },
      telephone: { ...requiredField, maskOptions: TELEPHONE_MASK_OPTIONS }
    }
  },
  dependents: {
    ...formSegment,
    index: 2,
    display: 'Spouse and Eligible Dependent',
    panel: <SpouseAndDependents />,
    fields: {
      spouseApplying: {
        ...requiredField,
        default: 'no'
      },
      childrenApplying: {
        ...requiredField,
        default: 'no'
      },
      spouse: {},
      children: []
    }
  },
  coverageAmount: {
    ...formSegment,
    index: 3,
    display: 'Coverage',
    panel: <Coverage />,
    fields: {
      employee: {},
      spouse: {},
      children: {}
    }
  },
  lifeDisability: {
    ...healthInformationFormSegment,
    index: 4,
    enabled: false,
    display: 'Life/Disability Health Information',
    panel: <LifeAndDisability />,
  },
  disability: {
    ...healthInformationFormSegment,
    index: 5,
    enabled: false,
    display: 'Disability Health Information',
    panel: <Disability />,
  },
  life: {
    ...healthInformationFormSegment,
    maxMedicalConditions: 12,
    index: 6,
    enabled: false,
    display: 'Life Health Information',
    panel: <Life />,
  },
  ci: {
    ...healthInformationFormSegment,
    maxMedicalConditions: 20,
    index: 7,
    enabled: false,
    display: 'Critical Illness/Specified Disease Health Information',
    panel: <CriticalIllness />,
  },
  verification: {
    ...formSegment,
    index: 8,
    display: 'Verification & Signature',
    panel: <Signature />,
    fields: {
      ready: { ...requiredField, include: false, pattern: '^true$' },
      agree: { ...requiredField, pattern: '^true$' },
      employeeElectronicSignature: { ...requiredField, errorDetail: 'Signature must match full name.' },
      spouseElectronicSignature: { ...optionalField, errorDetail: 'Signature must match full name.' },
      childrenElectronicSignature: { ...optionalField, errorDetail: 'Signature must match full name.' },
      employeeSignatureDate: {
        ...requiredField,
        value: toMMddyyyy_slashes(toMMddyyyy()),
        valid: true,
        maskOptions: {
          ...DATE_MASK_OPTIONS,
          maskOnSubmit: false
        }
      },
      spouseSignatureDate: {
        ...requiredField,
        value: toMMddyyyy_slashes(toMMddyyyy()),
        valid: true,
        maskOptions: {
          ...DATE_MASK_OPTIONS,
          maskOnSubmit: false
        }
      },
      childrenSignatureDate: {
        ...requiredField,
        value: toMMddyyyy_slashes(toMMddyyyy()),
        valid: true,
        maskOptions: {
          ...DATE_MASK_OPTIONS,
          maskOnSubmit: false
        }
      }
    }
  }
}